/**
 * Authentication Context Provider
 * Provides auth state and methods throughout the application
 */

import { createContext, useCallback, useEffect, useState, ReactNode } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import {
  login as authLogin,
  logout as authLogout,
  register as authRegister,
  loginWithGoogle as authLoginWithGoogle,
  resetPassword as authResetPassword,
  updateProfile as authUpdateProfile,
  subscribeToAuthChanges,
  getUserProfile,
  AuthResult,
} from '@/lib/auth';
import { isFirebaseConfigured } from '@/lib/firebase';
import {
  UserProfile,
  AuthCredentials,
  RegisterData,
  UpdateProfileData,
  AuthState,
} from '@/types/user';

interface AuthContextValue extends AuthState {
  login: (credentials: AuthCredentials) => Promise<AuthResult<UserProfile>>;
  loginWithGoogle: () => Promise<AuthResult<UserProfile>>;
  register: (data: RegisterData) => Promise<AuthResult<UserProfile>>;
  logout: () => Promise<AuthResult>;
  resetPassword: (email: string) => Promise<AuthResult>;
  updateProfile: (data: UpdateProfileData) => Promise<AuthResult<UserProfile>>;
  refreshProfile: () => Promise<void>;
  isConfigured: boolean;
}

const initialState: AuthState = {
  user: null,
  isLoading: true,
  isAuthenticated: false,
  error: null,
};

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>(initialState);
  const [isConfigured] = useState(() => isFirebaseConfigured());

  const refreshProfile = useCallback(async () => {
    if (!state.user?.id) return;

    const profile = await getUserProfile(state.user.id);
    if (profile) {
      setState((prev) => ({
        ...prev,
        user: profile,
      }));
    }
  }, [state.user?.id]);

  useEffect(() => {
    if (!isConfigured) {
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
        error: null,
      });
      return;
    }

    const unsubscribe = subscribeToAuthChanges(async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        const profile = await getUserProfile(firebaseUser.uid);
        setState({
          user: profile,
          isLoading: false,
          isAuthenticated: true,
          error: null,
        });
      } else {
        setState({
          user: null,
          isLoading: false,
          isAuthenticated: false,
          error: null,
        });
      }
    });

    return unsubscribe;
  }, [isConfigured]);

  const login = useCallback(async (credentials: AuthCredentials) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    const result = await authLogin(credentials);

    if (result.success && result.data) {
      setState({
        user: result.data,
        isLoading: false,
        isAuthenticated: true,
        error: null,
      });
    } else {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: result.error?.message || 'Login failed',
      }));
    }

    return result;
  }, []);

  const loginWithGoogle = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    const result = await authLoginWithGoogle();

    if (result.success && result.data) {
      setState({
        user: result.data,
        isLoading: false,
        isAuthenticated: true,
        error: null,
      });
    } else {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: result.error?.message || 'Google sign-in failed',
      }));
    }

    return result;
  }, []);

  const register = useCallback(async (data: RegisterData) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    const result = await authRegister(data);

    if (result.success && result.data) {
      setState({
        user: result.data,
        isLoading: false,
        isAuthenticated: true,
        error: null,
      });
    } else {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: result.error?.message || 'Registration failed',
      }));
    }

    return result;
  }, []);

  const logout = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true }));

    const result = await authLogout();

    if (result.success) {
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
        error: null,
      });
    } else {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: result.error?.message || 'Logout failed',
      }));
    }

    return result;
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const result = await authResetPassword(email);
    return result;
  }, []);

  const updateProfile = useCallback(
    async (data: UpdateProfileData) => {
      if (!state.user?.id) {
        return {
          success: false,
          error: { code: 'auth/user-not-found' as const, message: 'No user logged in' },
        };
      }

      const result = await authUpdateProfile(state.user.id, data);

      if (result.success && result.data) {
        setState((prev) => ({
          ...prev,
          user: result.data!,
        }));
      }

      return result;
    },
    [state.user?.id]
  );

  const value: AuthContextValue = {
    ...state,
    login,
    loginWithGoogle,
    register,
    logout,
    resetPassword,
    updateProfile,
    refreshProfile,
    isConfigured,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
